/**
 * Batch Export Utilities
 *
 * Combines multiple artefacts into single documents (PowerPoint, Word, PDF)
 * Leverages existing export utilities for individual formats
 * Supports HTML artefacts as high-quality embedded images
 */

import type { RuntimeBrand as Brand } from "@webuild-ai/react-ui/types";

import PptxGenJS from "pptxgenjs";
import { Document, Packer, Paragraph, HeadingLevel, ImageRun, type Table } from "docx";
import DOMPurify from "dompurify";

import { Artefact, ArtefactType } from "@/types/artefacts";
import { sanitizeUnsupportedColors } from "@/utils/styleUtils";
import { SANITISE_CONFIG } from "@/config/sanitise";
import { escapeHtml } from "@/utils/htmlEscape";
import { inlineMarkdownToHtml } from "@/utils/inlineMarkdown";
import { parseTableContent } from "@/utils/tableUtils";
import { logger } from "@/telemetry";
import { convertMarkdownToDocx, convertMarkdownToDocxParagraphs } from "@/utils/markdownToDocx";
import { renderMarkdownToPdf } from "@/utils/pdfRenderer";
import { markdownToHtml } from "@/utils/markdownConverter";
import { addMarkdownSlidesToPresentation } from "@/utils/markdownToPptx";
import {
  captureHtmlArtefactsAsImages,
  createImageMap,
  type CapturedImage,
  CAPTURE_WIDTH_PDF,
  CAPTURE_WIDTH_POWERPOINT,
  PAGE_CAPTURE_WIDTHS,
} from "@/utils/artefactExport/htmlToImage";
import { batchExportToExcel, type ExcelExportLabels } from "@/utils/artefactExport/excelExport";
import {
  resolveTemplate,
  applyWordTemplate,
  prepareTemplateData,
  applyPptxTemplate,
  preparePptxTemplateData,
  applyExcelTemplate,
  prepareExcelTemplateData,
} from "@/utils/templateEngine";
import {
  PDF_PRIMARY_COLOUR,
  PDF_BATCH_TITLE_STYLE,
  PDF_BATCH_CODE_STYLE,
  PDF_BATCH_CONTENT_STYLE,
  PDF_TABLE_BORDER_COLOUR,
  PDF_TABLE_EVEN_ROW_COLOUR,
  PDF_PAGEBREAK_CONFIG_WITH_IMAGES,
} from "@/utils/pdfStyles";

// PowerPoint slide configuration constants (in inches)
const PPTX_SLIDE_WIDTH = 10; // Standard PowerPoint slide width
const PPTX_SLIDE_HEIGHT = 7.5; // Standard PowerPoint slide height
const PPTX_MARGIN_HORIZONTAL = 0.5; // Left/right margin
const PPTX_MARGIN_TOP = 0.5; // Top margin

// PowerPoint font sizes
const PPTX_TITLE_SLIDE_MAIN_SIZE = 44; // Main title font size
const PPTX_TITLE_SLIDE_SUB_SIZE = 20; // Subtitle font size
const PPTX_CODE_FONT_SIZE = 12; // Code content font size

// PowerPoint positioning
const PPTX_TITLE_SLIDE_Y_MAIN = 1.5; // Main title Y position
const PPTX_TITLE_SLIDE_Y_SUB = 2.5; // Subtitle Y position

// PowerPoint colour palette (hex without #)
const PPTX_COLOUR_TITLE = "28326E"; // Navy — slide titles and headings
const PPTX_COLOUR_SUBTITLE = "666666"; // Grey — subtitle text
const PPTX_COLOUR_BODY = "1a1a1a"; // Near-black — code / body text

// Word document configuration constants (in pixels)
// A4 content area: 160mm wide (~604px at 96 DPI)
// These values should match the capture widths for proper scaling
const WORD_IMAGE_MAX_WIDTH = 580; // Slightly smaller than capture width to ensure fit with margins
const WORD_IMAGE_MAX_HEIGHT = 800; // Max height to prevent overly tall images
const WORD_HEADING_SPACING_AFTER = 200; // Spacing after headings

// PDF configuration constants
const PDF_MARGIN_MM = 10; // PDF page margin in millimeters
const PDF_IMAGE_QUALITY = 0.98; // JPEG quality for images in PDF
const PDF_HTML2CANVAS_SCALE = 2; // Scale for html2canvas rendering
const PDF_IMAGE_MARGIN_PX = 20; // Image margin in pixels

export type BatchExportFormat = "powerpoint" | "word" | "pdf" | "excel";

export interface BatchExportOptions {
  format: BatchExportFormat;
  title?: string;
  author?: string;
  includeTableOfContents?: boolean;
  useSeparateWorksheets?: boolean; // Excel-specific option
  itemsLabel?: string; // Translated label for "{count} items" subtitle (PowerPoint title slide)
  brand?: Brand; // For Excel styling
  brandName?: string; // Brand name for template resolution
  theme?: "light" | "dark"; // For Excel styling
  fontFamily?: string; // Font for Word export
  codeFontFamily?: string; // Code font for Word export
  pageSize?: "A4" | "Letter" | "Legal"; // Page size for Word export (default: A4)
  excelLabels?: ExcelExportLabels; // Translated labels for Excel export
}

/**
 * Generate filename with timestamp for batch exports
 */
function generateBatchFilename(format: BatchExportFormat): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
  const dateStr = timestamp[0];
  const timeStr = timestamp[1].substring(0, 8);
  const extensions = {
    powerpoint: "pptx",
    word: "docx",
    pdf: "pdf",
    excel: "xlsx",
  };

  return `batch_export_${dateStr}_${timeStr}.${extensions[format]}`;
}

/**
 * Combine multiple artefacts into single markdown document
 * Handles different artefact types appropriately
 * Note: HTML artefacts are skipped as they will be embedded as images
 */
export function combineArtefactsToMarkdown(
  artefacts: Artefact[],
  options: { includeTableOfContents?: boolean } = {},
): string {
  const sections: string[] = [];

  // Filter out HTML artefacts as they will be handled separately as images
  const markdownArtefacts = artefacts.filter((a) => a.type !== ArtefactType.HTML);

  // Optional table of contents
  if (options.includeTableOfContents && artefacts.length > 1) {
    sections.push("# Table of Contents\n");
    artefacts.forEach((artefact, index) => {
      sections.push(
        `${index + 1}. [${artefact.title}](#${artefact.title.toLowerCase().replace(/\s+/g, "-")})`,
      );
    });
    sections.push("\n---\n");
  }

  // Combine all artefacts
  markdownArtefacts.forEach((artefact, index) => {
    // Skip adding artefact title as heading - the content itself typically contains
    // the title already, and adding it creates duplicate headings in exports.
    // The document title is added separately by the export functions.

    // Add type-specific content formatting
    switch (artefact.type) {
      case ArtefactType.CODE:
        // Wrap code in code blocks with language
        sections.push(`\`\`\`${artefact.language || ""}\n${artefact.content}\n\`\`\`\n`);
        break;

      case ArtefactType.TABLE:
        // Tables are already in markdown format
        sections.push(artefact.content + "\n");
        break;

      case ArtefactType.DOCUMENT:
        // Documents are already markdown
        sections.push(artefact.content + "\n");
        break;

      default:
        sections.push(artefact.content + "\n");
    }

    // Add page/slide break between artefacts (except for last)
    if (index < markdownArtefacts.length - 1) {
      sections.push("\n---\n\n");
    }
  });

  return sections.join("\n");
}

type PowerPointExportOptions = Pick<
  BatchExportOptions,
  "title" | "author" | "fontFamily" | "codeFontFamily" | "brandName" | "itemsLabel"
>;

/** The fonts a programmatic PowerPoint export passes to each slide. */
interface PowerPointFonts {
  /** undefined leaves PowerPoint's default. */
  fontFamily: string | undefined;
  codeFontFamily: string;
}

/**
 * Capture the HTML artefacts (charts/visualisations) as images for a PowerPoint
 * export. Tables are rendered as native OOXML tables in both the template path
 * and the programmatic (PptxGenJS) path, so only HTML artefacts need capture.
 * Unlike the Word export, a capture failure is fatal here.
 */
async function capturePowerPointImages(artefacts: Artefact[]): Promise<Map<string, CapturedImage>> {
  const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);

  if (htmlArtefacts.length === 0) return new Map<string, CapturedImage>();

  try {
    const capturedImages = await captureHtmlArtefactsAsImages(htmlArtefacts, {
      width: CAPTURE_WIDTH_POWERPOINT,
    });

    return createImageMap(capturedImages);
  } catch (error) {
    logger.error("Failed to capture HTML artefacts as images:", error);
    throw new Error(
      `Failed to capture charts: ${error instanceof Error ? error.message : String(error)}`,
      { cause: error },
    );
  }
}

/** Convert the CapturedImage map to the PptxImageData map the template engine takes. */
function toPptxImageMap(imageMap: Map<string, CapturedImage>) {
  return new Map(
    [...imageMap.entries()].map(([id, img]) => [
      id,
      { imageData: img.imageData, width: img.width, height: img.height },
    ]),
  );
}

/** Save a finished export through a temporary download link. */
function downloadBatchBlob(blob: Blob, format: BatchExportFormat): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const filename = generateBatchFilename(format);

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Template-first path: if a branded PPTX template is available, render and
 * download the deck from it. Returns false to fall back to the programmatic
 * export, which includes any failure after the template was found, the
 * download itself among them.
 */
async function exportPowerPointFromTemplate(
  artefacts: Artefact[],
  options: PowerPointExportOptions,
  imageMap: Map<string, CapturedImage>,
): Promise<boolean> {
  if (!options.brandName) return false;

  try {
    const template = await resolveTemplate("pptx", options.brandName);

    if (!template) return false;

    const pptxImageMap = toPptxImageMap(imageMap);
    const data = preparePptxTemplateData(artefacts, {
      title: options.title,
      author: options.author,
      brand: options.brandName,
      fontFamily: options.fontFamily,
      imageMap: pptxImageMap.size > 0 ? pptxImageMap : undefined,
    });

    const blob = await applyPptxTemplate(template.data, data);

    downloadBatchBlob(blob, "powerpoint");

    return true;
  } catch (error) {
    logger.warn("Template export failed, falling back to programmatic export", {
      error: String(error),
    });

    return false;
  }
}

/**
 * The title slide: the user's document title over the item count, or over the
 * translated items label when one is given. Nothing is added without a title.
 */
function addPowerPointTitleSlide(
  pptx: PptxGenJS,
  options: PowerPointExportOptions,
  artefactCount: number,
): void {
  const { title, fontFamily } = options;

  if (!title) return;

  const titleSlide = pptx.addSlide();

  titleSlide.addText(title, {
    x: PPTX_MARGIN_HORIZONTAL,
    y: PPTX_TITLE_SLIDE_Y_MAIN,
    w: "90%",
    h: 1,
    fontSize: PPTX_TITLE_SLIDE_MAIN_SIZE,
    bold: true,
    color: PPTX_COLOUR_TITLE,
    ...(fontFamily && { fontFace: fontFamily }),
  });
  titleSlide.addText(options.itemsLabel ?? `${artefactCount} items`, {
    x: PPTX_MARGIN_HORIZONTAL,
    y: PPTX_TITLE_SLIDE_Y_SUB,
    w: "90%",
    fontSize: PPTX_TITLE_SLIDE_SUB_SIZE,
    color: PPTX_COLOUR_SUBTITLE,
    ...(fontFamily && { fontFace: fontFamily }),
  });
}

/**
 * Fit a captured image within the slide's margins, preserving its aspect ratio
 * (standard slide is 10" x 7.5").
 */
function fitImageToPowerPointSlide(image: CapturedImage): { width: number; height: number } {
  const aspectRatio = image.height / image.width;
  const maxWidth = PPTX_SLIDE_WIDTH - 2 * PPTX_MARGIN_HORIZONTAL; // Leave margins on each side
  const maxHeight = PPTX_SLIDE_HEIGHT - 2 * PPTX_MARGIN_TOP; // Full slide height minus margins

  if (aspectRatio > maxHeight / maxWidth) {
    // Tall image: constrain by height
    return { width: maxHeight / aspectRatio, height: maxHeight };
  }

  // Wide image: constrain by width
  return { width: maxWidth, height: maxWidth * aspectRatio };
}

/** An HTML artefact as a slide holding only its captured image (no per-artefact title). */
function addPowerPointImageSlide(pptx: PptxGenJS, image: CapturedImage): void {
  const slide = pptx.addSlide();
  const { width, height } = fitImageToPowerPointSlide(image);

  slide.addImage({
    data: image.base64,
    x: (PPTX_SLIDE_WIDTH - width) / 2, // Centre horizontally
    y: PPTX_MARGIN_TOP,
    w: width,
    h: height,
  });
}

/** A CODE artefact as a single slide in a monospace font, kept as-is for literal display. */
function addPowerPointCodeSlide(pptx: PptxGenJS, code: string, codeFontFamily: string): void {
  const slide = pptx.addSlide();

  slide.addText(code, {
    x: PPTX_MARGIN_HORIZONTAL,
    y: PPTX_MARGIN_TOP,
    w: "90%",
    h: "90%",
    fontSize: PPTX_CODE_FONT_SIZE,
    fontFace: codeFontFamily,
    valign: "top",
    color: PPTX_COLOUR_BODY,
  });
}

/**
 * One artefact's slides. An HTML artefact whose image was not captured adds
 * none, and DOCUMENT and TABLE artefacts go through the multi-slide markdown
 * conversion, which spreads content across slides when needed. Per-artefact
 * calculated titles are intentionally omitted from exports.
 */
async function addPowerPointArtefactSlides(
  pptx: PptxGenJS,
  artefact: Artefact,
  imageMap: Map<string, CapturedImage>,
  fonts: PowerPointFonts,
): Promise<void> {
  if (artefact.type === ArtefactType.HTML) {
    const image = imageMap.get(artefact.id);

    if (image) addPowerPointImageSlide(pptx, image);

    return;
  }

  if (artefact.type === ArtefactType.CODE) {
    addPowerPointCodeSlide(pptx, artefact.content, fonts.codeFontFamily);

    return;
  }

  // Skip the per-artefact title slide; only the presentation title slide is shown
  await addMarkdownSlidesToPresentation(pptx, artefact.content, {
    skipTitleSlide: true,
    fontFamily: fonts.fontFamily,
    codeFontFamily: fonts.codeFontFamily,
  });
}

/**
 * Export multiple artefacts to PowerPoint presentation
 * HTML artefacts are captured as images and embedded. Every path through it,
 * including the template fallback, is pinned by batchExportToPowerPoint.golden.test.ts.
 */
export async function batchExportToPowerPoint(
  artefacts: Artefact[],
  options: PowerPointExportOptions = {},
): Promise<void> {
  if (artefacts.length === 0) {
    throw new Error("No artefacts to export");
  }

  const imageMap = await capturePowerPointImages(artefacts);

  // Template resolution: if a PPTX template is available, use it instead of programmatic export
  if (await exportPowerPointFromTemplate(artefacts, options, imageMap)) return;

  // Create presentation and process artefacts in original order
  const finalPptx = new PptxGenJS();
  const fonts: PowerPointFonts = {
    fontFamily: options.fontFamily,
    codeFontFamily: options.codeFontFamily || "Courier New",
  };

  addPowerPointTitleSlide(finalPptx, options, artefacts.length);

  // Sequential, so the slides land in artefact order
  for (const artefact of artefacts) {
    await addPowerPointArtefactSlides(finalPptx, artefact, imageMap, fonts);
  }

  const filename = generateBatchFilename("powerpoint");

  await finalPptx.writeFile({ fileName: filename });
}

type WordExportOptions = Pick<
  BatchExportOptions,
  | "title"
  | "author"
  | "includeTableOfContents"
  | "fontFamily"
  | "codeFontFamily"
  | "pageSize"
  | "brandName"
>;

/** Word page sizes in twips. */
const WORD_PAGE_SIZES = {
  A4: { width: 11906, height: 16838 },
  Letter: { width: 12240, height: 15840 },
  Legal: { width: 12240, height: 20160 },
};

/**
 * Capture HTML artefact images for a Word export. A capture failure is not
 * fatal: the template path degrades to text and the programmatic path may
 * still work, so an empty map comes back instead.
 */
async function captureWordImages(
  htmlArtefacts: Artefact[],
  pageSize: WordExportOptions["pageSize"],
): Promise<Map<string, CapturedImage>> {
  if (htmlArtefacts.length === 0) return new Map<string, CapturedImage>();

  try {
    const capturedImages = await captureHtmlArtefactsAsImages(htmlArtefacts, {
      width: PAGE_CAPTURE_WIDTHS[pageSize || "A4"],
    });

    return createImageMap(capturedImages);
  } catch (error) {
    logger.warn("HTML image capture failed, charts will render as text", {
      error: String(error),
    });

    return new Map<string, CapturedImage>();
  }
}

/**
 * Template-first path: if a branded template is available, render the entire
 * document via docxtemplater. The template's {#artefacts} loop drives content
 * layout. HTML artefact images are injected post-render when available.
 * Returns undefined to fall back to the programmatic export.
 */
async function exportWordFromTemplate(
  artefacts: Artefact[],
  options: WordExportOptions,
  imageMap: Map<string, CapturedImage>,
): Promise<Blob | undefined> {
  if (!options.brandName) return undefined;

  try {
    const template = await resolveTemplate("word", options.brandName);

    if (!template) return undefined;

    const data = prepareTemplateData(artefacts, {
      title: options.title,
      author: options.author,
      brand: options.brandName,
    });

    // Awaited so a render rejection lands in the catch and falls back.
    return await applyWordTemplate(template.data, data, imageMap.size > 0 ? imageMap : undefined);
  } catch (error) {
    logger.warn("Template-first export failed, falling back to programmatic export", {
      error: String(error),
    });

    return undefined;
  }
}

/**
 * Fit a captured image within the Word page's maximum width and height,
 * preserving its aspect ratio. Images captured at 2x scale have excellent quality.
 */
function fitImageToWordPage(image: CapturedImage): { width: number; height: number } {
  const aspectRatio = image.width / image.height;

  if (aspectRatio > WORD_IMAGE_MAX_WIDTH / WORD_IMAGE_MAX_HEIGHT) {
    // Wide image: constrain by width
    return { width: WORD_IMAGE_MAX_WIDTH, height: Math.round(WORD_IMAGE_MAX_WIDTH / aspectRatio) };
  }

  // Tall image: constrain by height
  return { width: Math.round(WORD_IMAGE_MAX_HEIGHT * aspectRatio), height: WORD_IMAGE_MAX_HEIGHT };
}

/**
 * The document body: the title heading when one is given, then each artefact
 * in order, HTML as its captured image and everything else as converted
 * markdown. Per-artefact calculated titles are intentionally omitted.
 */
async function buildWordSections(
  artefacts: Artefact[],
  options: WordExportOptions,
  imageMap: Map<string, CapturedImage>,
): Promise<Array<Paragraph | Table>> {
  // Title heading, only added when the user provides a document title
  const sections: Array<Paragraph | Table> = options.title ? [wordTitleHeading(options.title)] : [];
  const fontContext = wordFontContext(options);

  // Sequential, so the conversions run and land in artefact order
  for (const artefact of artefacts) {
    sections.push(...(await wordArtefactSections(artefact, fontContext, imageMap)));
  }

  return sections;
}

/** The document's level-one title heading. */
function wordTitleHeading(title: string): Paragraph {
  return new Paragraph({
    text: title,
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 0, after: WORD_HEADING_SPACING_AFTER },
  });
}

/** The fonts passed to the markdown conversion, when the export names one. */
function wordFontContext(options: WordExportOptions) {
  return options.fontFamily
    ? {
        fontFamily: options.fontFamily,
        codeFontFamily: options.codeFontFamily || "Courier New",
      }
    : undefined;
}

/**
 * One artefact's sections: converted markdown for anything but HTML, and for
 * HTML its captured image, or nothing when no image was captured.
 */
async function wordArtefactSections(
  artefact: Artefact,
  fontContext: ReturnType<typeof wordFontContext>,
  imageMap: Map<string, CapturedImage>,
): Promise<Array<Paragraph | Table>> {
  if (artefact.type !== ArtefactType.HTML) {
    const { paragraphs } = await convertMarkdownToDocxParagraphs(artefact.content, fontContext);

    return paragraphs;
  }

  const image = imageMap.get(artefact.id);

  return image ? [wordImageParagraph(image)] : [];
}

/** A captured HTML artefact embedded as a page-fitted PNG, with no spacing after it. */
function wordImageParagraph(image: CapturedImage): Paragraph {
  return new Paragraph({
    children: [
      new ImageRun({
        data: image.imageData,
        transformation: fitImageToWordPage(image),
        type: "png",
      }),
    ],
    spacing: { after: 0 },
  });
}

/** Pure markdown conversion path, used when there is no HTML (better formatting). */
function exportWordFromMarkdown(artefacts: Artefact[], options: WordExportOptions): Promise<Blob> {
  const combinedMarkdown = combineArtefactsToMarkdown(artefacts, {
    includeTableOfContents: options.includeTableOfContents,
  });

  const fullMarkdown = options.title
    ? `# ${options.title}\n\n${combinedMarkdown}`
    : combinedMarkdown;

  return convertMarkdownToDocx(fullMarkdown, {
    title: options.title || "",
    // Fall back to the brand name rather than a hardcoded vendor name, so a
    // white-label export never stamps another brand into the file metadata.
    creator: options.author || options.brandName || "",
    description: `Export of ${artefacts.length} items`,
    fontFamily: options.fontFamily,
    codeFontFamily: options.codeFontFamily,
    pageSize: options.pageSize,
  });
}

/**
 * Export multiple artefacts to Word document
 * HTML artefacts are captured as images and embedded. Every path through it,
 * including the template fallback, is pinned by batchExportToWord.golden.test.ts.
 */
export async function batchExportToWord(
  artefacts: Artefact[],
  options: WordExportOptions = {},
): Promise<Blob> {
  if (artefacts.length === 0) {
    throw new Error("No artefacts to export");
  }

  // Capture HTML artefact images upfront — needed by both template and
  // programmatic export paths. Table artefacts use native OOXML tables in
  // the template path (not screenshots) so they're excluded from capture.
  const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);
  const nonHtmlArtefacts = artefacts.filter((a) => a.type !== ArtefactType.HTML);
  const imageMap = await captureWordImages(htmlArtefacts, options.pageSize);

  const templated = await exportWordFromTemplate(artefacts, options, imageMap);

  if (templated) return templated;

  const sections = await buildWordSections(artefacts, options, imageMap);

  // If we have non-HTML artefacts, use the existing markdown converter for better formatting
  if (nonHtmlArtefacts.length > 0 && htmlArtefacts.length === 0) {
    return exportWordFromMarkdown(artefacts, options);
  }

  // Mixed content or pure HTML - use manual construction
  const pageSize = WORD_PAGE_SIZES[options.pageSize || "A4"];
  const doc = new Document({
    creator: options.author || options.brandName || "",
    title: options.title || "",
    description: `Export of ${artefacts.length} items`,
    sections: [
      {
        properties: {
          page: {
            size: {
              width: pageSize.width,
              height: pageSize.height,
            },
          },
        },
        children: sections,
      },
    ],
  });

  return Packer.toBlob(doc);
}

/**
 * Export multiple artefacts to PDF document
 * HTML artefacts are captured as images and embedded
 *
 * Note: This function is excluded from coverage because it requires complex
 * mocking of html2pdf.js library and markdown conversion pipelines.
 * The function is tested through integration testing in the UI.
 */
/* istanbul ignore next */
export async function batchExportToPDF(
  artefacts: Artefact[],
  options: Pick<BatchExportOptions, "title" | "author" | "includeTableOfContents" | "brand"> = {},
): Promise<any> {
  if (artefacts.length === 0) {
    throw new Error("No artefacts to export");
  }

  // For PDF, we'll use the markdown converter for non-HTML artefacts
  // TABLE artefacts are converted to styled HTML and captured as images
  // so they render with the same look as the in-app table component.
  const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);
  const tableArtefacts = artefacts.filter((a) => a.type === ArtefactType.TABLE);

  // Convert TABLE artefacts to styled HTML so they can be image-captured
  const tableAsHtmlArtefacts: Artefact[] = tableArtefacts
    .map((artefact) => {
      const tableData = parseTableContent(artefact.content);

      if (!tableData) {
        logger.warn("Failed to parse TABLE artefact content for PDF export", {
          id: artefact.id,
          title: artefact.title,
        });

        return null;
      }

      const tableHtml = `<html><head><style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; margin: 20px; color: #1a1a1a; }
        table { border-collapse: collapse; width: 100%; }
        th { padding: 10px 14px; text-align: left; font-weight: 600; color: #ffffff; background-color: ${PDF_PRIMARY_COLOUR}; border: 1px solid ${PDF_PRIMARY_COLOUR}; font-size: 13px; }
        td { padding: 10px 14px; text-align: left; border: 1px solid ${PDF_TABLE_BORDER_COLOUR}; font-size: 13px; }
        tr:nth-child(even) { background-color: ${PDF_TABLE_EVEN_ROW_COLOUR}; }
        td a, th a { color: inherit; text-decoration: underline; }
        td code, th code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
      </style></head><body><table>
        <thead><tr>${tableData.headers.map((h) => `<th>${inlineMarkdownToHtml(h)}</th>`).join("")}</tr></thead>
        <tbody>${tableData.rows.map((row) => `<tr>${tableData.headers.map((h) => `<td>${inlineMarkdownToHtml(String(row[h] ?? ""))}</td>`).join("")}</tr>`).join("")}</tbody>
      </table></body></html>`;

      return { ...artefact, type: ArtefactType.HTML, content: tableHtml } as Artefact;
    })
    .filter(Boolean) as Artefact[];

  const allImageArtefacts = [...htmlArtefacts, ...tableAsHtmlArtefacts];

  if (allImageArtefacts.length === 0) {
    // No HTML or TABLE artefacts - use standard markdown conversion
    const combinedMarkdown = combineArtefactsToMarkdown(artefacts, {
      includeTableOfContents: options.includeTableOfContents,
    });

    const fullMarkdown = options.title
      ? `# ${options.title}\n\n${combinedMarkdown}`
      : combinedMarkdown;

    return renderMarkdownToPdf(fullMarkdown, {
      title: options.title || "",
      author: options.author || "",
      brandFontFamily: options.brand?.fontFamily,
      brandCodeFontFamily: options.brand?.codeFontFamily,
    });
  }

  // Capture HTML and TABLE artefacts as images with PDF-optimised width
  const capturedImages = await captureHtmlArtefactsAsImages(allImageArtefacts, {
    width: CAPTURE_WIDTH_PDF,
  });
  const imageMap = createImageMap(capturedImages);

  // For PDF with HTML content, we need to use a different approach
  // Using html2pdf.js for better image embedding support
  const html2pdf = (await import("html2pdf.js")).default;

  // Build HTML content for PDF generation - minimal structure to avoid blank pages
  const title = options.title || "";

  // Build content sections
  const sections: string[] = [];

  for (const artefact of artefacts) {
    let sectionHtml = "";

    // Per-artefact calculated titles are intentionally omitted from exports.
    // Only the document-level title (from user input) appears as a heading.

    if (artefact.type === ArtefactType.HTML || artefact.type === ArtefactType.TABLE) {
      // HTML and TABLE artefacts are captured as images for pixel-perfect rendering
      const image = imageMap.get(artefact.id);

      if (image) {
        sectionHtml += `<img src="${image.base64}" style="max-width: 100%; height: auto; margin: ${PDF_IMAGE_MARGIN_PX}px 0; display: block;" />`;
      }
    } else if (artefact.type === ArtefactType.CODE) {
      sectionHtml += `<pre style="${PDF_BATCH_CODE_STYLE}"><code>${escapeHtml(artefact.content)}</code></pre>`;
    } else {
      const convertedHtml = DOMPurify.sanitize(markdownToHtml(artefact.content), SANITISE_CONFIG);

      sectionHtml += `<div style="${PDF_BATCH_CONTENT_STYLE}">${convertedHtml}</div>`;
    }

    sections.push(sectionHtml);
  }

  // Build a complete HTML document structure (like renderMarkdownToPdf does)
  // This is required for html2pdf.js to render properly
  const htmlContent = `
    <html>
      <head>
        <meta charset="UTF-8">
        <style data-pdf-export>
          /* Prevent page breaks inside elements to avoid canvas slicing artifacts */
          p, li, h1, h2, h3, h4, h5, h6, tr, blockquote, pre, code, ul, ol {
            page-break-inside: avoid;
          }

          /* Keep headings with their following content */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
          }

          /* Prevent orphaned headings and content */
          p {
            orphans: 3;
            widows: 3;
          }

          /* Code blocks keep their grey background */
          pre, code {
            background-color: #f5f5f5;
          }

          /* HR separators */
          hr {
            margin: 24px 0;
            padding: 0;
            border-top: 1px solid #ddd;
          }

          /* Reset all CSS custom properties that might use oklch */
          :root, html, body, * {
            --background: #f5f5f5;
            --foreground: #1a1a2e;
            --surface: #ffffff;
            --default: #e5e5ea;
          }

          /* Base body styles */
          body {
            font-family: Arial, sans-serif;
            color: #000;
            background: #ffffff;
            margin: 0;
            padding: 0;
          }

          /* Table styles — branded, centred */
          table {
            border-collapse: collapse;
            margin: 16px auto;
            table-layout: auto;
            font-size: 11px;
          }

          thead {
            background-color: ${PDF_PRIMARY_COLOUR};
          }

          th {
            font-weight: bold;
            color: #ffffff;
            background-color: ${PDF_PRIMARY_COLOUR};
            padding: 8px 12px;
            border: 1px solid ${PDF_PRIMARY_COLOUR};
            text-align: left;
          }

          td {
            border: 1px solid ${PDF_TABLE_BORDER_COLOUR};
            padding: 6px 12px;
            text-align: left;
          }

          tr:nth-child(even) {
            background-color: ${PDF_TABLE_EVEN_ROW_COLOUR};
          }

          /* Image styles */
          img {
            max-width: 100%;
            height: auto;
          }
        </style>
      </head>
      <body>
        ${title ? `<h1 style="${PDF_BATCH_TITLE_STYLE}">${escapeHtml(title)}</h1>` : ""}
        ${sections.join('<hr style="margin: 24px 0; border: none; border-top: 1px solid #ddd;">')}
      </body>
    </html>
  `;

  const opt = {
    margin: PDF_MARGIN_MM,
    filename: generateBatchFilename("pdf"),
    image: { type: "jpeg" as const, quality: PDF_IMAGE_QUALITY },
    html2canvas: {
      scale: PDF_HTML2CANVAS_SCALE,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      windowWidth: 800,
      // html2canvas clones the entire document (including HeroUI v3 stylesheets
      // that use oklch()). After cloning, strip those stylesheets so the colour
      // parser never encounters oklch — it only supports rgb/hsl.
      onclone: (_clonedDoc: unknown) => {
        const doc = _clonedDoc as globalThis.Document;

        doc
          .querySelectorAll('style:not([data-pdf-export]), link[rel="stylesheet"]')
          .forEach((el: Element) => el.remove());
        sanitizeUnsupportedColors(doc);
      },
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
    pagebreak: PDF_PAGEBREAK_CONFIG_WITH_IMAGES,
  };

  const pdfDoc = await html2pdf().set(opt).from(htmlContent).toPdf().get("pdf");

  if (options.title) {
    pdfDoc.setProperties({ title: options.title });
  }
  if (options.author) {
    pdfDoc.setProperties({ author: options.author });
  }

  return pdfDoc;
}

/**
 * Export multiple artefacts to PDF and return as ArrayBuffer for preview
 * This is a wrapper around batchExportToPDF that returns data instead of auto-downloading
 */
/* istanbul ignore next */
export async function batchExportToPDFBlob(
  artefacts: Artefact[],
  options: Pick<BatchExportOptions, "title" | "author" | "includeTableOfContents"> = {},
): Promise<ArrayBuffer> {
  const pdfDoc = await batchExportToPDF(artefacts, options);

  return pdfDoc.output("arraybuffer");
}

/**
 * Export multiple artefacts to Word and return as ArrayBuffer for preview
 * This is a wrapper around batchExportToWord that returns ArrayBuffer instead of Blob
 */
/* istanbul ignore next */
export async function batchExportToWordBlob(
  artefacts: Artefact[],
  options: Pick<
    BatchExportOptions,
    "title" | "author" | "includeTableOfContents" | "fontFamily" | "codeFontFamily" | "brandName"
  > = {},
): Promise<ArrayBuffer> {
  const blob = await batchExportToWord(artefacts, options);

  return blob.arrayBuffer();
}

/**
 * Unified batch export function
 *
 * Note: This function is excluded from coverage because it delegates to library-specific
 * export functions (PowerPoint, Word, PDF) which are integration-tested in the UI.
 * Only the Excel branch can be unit tested due to ExcelJS mock compatibility.
 */
/* istanbul ignore next */
export async function batchExport(
  artefacts: Artefact[],
  options: BatchExportOptions,
): Promise<void> {
  switch (options.format) {
    case "powerpoint":
      await batchExportToPowerPoint(artefacts, {
        title: options.title,
        author: options.author,
        fontFamily: options.fontFamily,
        codeFontFamily: options.codeFontFamily,
        brandName: options.brandName,
        itemsLabel: options.itemsLabel,
      });
      break;

    case "word": {
      const blob = await batchExportToWord(artefacts, options);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const filename = generateBatchFilename("word");

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      break;
    }

    case "pdf": {
      const doc = await batchExportToPDF(artefacts, options);
      const filename = generateBatchFilename("pdf");

      doc.save(filename);
      break;
    }

    case "excel": {
      // Template resolution: if an Excel template is available, use it
      if (options.brandName) {
        try {
          const template = await resolveTemplate("xlsx", options.brandName);

          if (template) {
            const data = prepareExcelTemplateData(artefacts, {
              title: options.title,
              author: options.author,
              brand: options.brandName,
              fontFamily: options.fontFamily,
            });

            // Capture HTML artefacts as images for template embedding
            const templateHtmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);
            let templateImageMap: Map<string, CapturedImage> | undefined;

            if (templateHtmlArtefacts.length > 0) {
              try {
                const capturedImages = await captureHtmlArtefactsAsImages(templateHtmlArtefacts, {
                  width: PAGE_CAPTURE_WIDTHS.A4,
                });

                templateImageMap = createImageMap(capturedImages);
              } catch (error) {
                logger.warn("Failed to capture chart images for Excel template export:", {
                  error: String(error),
                });
              }
            }

            const blob = await applyExcelTemplate(template.data, data, templateImageMap, {
              useSeparateWorksheets: options.useSeparateWorksheets,
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const filename = generateBatchFilename("excel");

            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            break;
          }
        } catch (error) {
          logger.warn("Excel template export failed, falling back to programmatic export", {
            error: String(error),
          });
        }
      }

      // Excel export requires brand and theme for styling
      if (!options.brand || !options.theme) {
        throw new Error("Brand and theme are required for Excel export");
      }

      // Capture HTML artefacts as images (for dimension info in placeholders)
      // Excel uses A4-based width as images are embedded similarly to documents
      const htmlArtefacts = artefacts.filter((a) => a.type === ArtefactType.HTML);
      let imageMap: Map<string, any> | undefined;

      if (htmlArtefacts.length > 0) {
        try {
          const capturedImages = await captureHtmlArtefactsAsImages(htmlArtefacts, {
            width: PAGE_CAPTURE_WIDTHS.A4,
          });

          imageMap = createImageMap(capturedImages);
        } catch (error) {
          logger.warn("Failed to capture chart images for Excel export:", { error: String(error) });
          // Continue without images - will show placeholder text
        }
      }

      await batchExportToExcel(
        artefacts,
        {
          useSeparateWorksheets: options.useSeparateWorksheets,
          fontFamily: options.fontFamily,
          codeFontFamily: options.codeFontFamily,
          labels: options.excelLabels,
        },
        options.title || "",
        options.brand,
        options.theme,
        imageMap,
      );
      break;
    }

    default:
      throw new Error(`Unsupported export format: ${options.format}`);
  }
}
